import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { api } from '../../api/client';
import TableViewport from '../common/TableViewport';
import DisabledReason from '../common/DisabledReason';
import RefreshButton from '../common/RefreshButton';
import { formatNumber } from '../../utils/format';

const ADMIN_KEY = process.env.REACT_APP_ADMIN_KEY;
const ADMIN_HEADERS = ADMIN_KEY ? { headers: { 'X-Admin-Key': ADMIN_KEY } } : {};

const RELATION_BIAS_OPTIONS = [
  { value: 0.1, label: 'Prefer (+0.10)' },
  { value: 0.05, label: 'Slightly prefer (+0.05)' },
  { value: 0, label: 'Neutral' },
  { value: -0.05, label: 'Slightly avoid (-0.05)' },
  { value: -0.1, label: 'Avoid (-0.10)' },
];

const SEASONAL_BIAS_OPTIONS = [
  { value: 0.15, label: 'Strong boost (+0.15)' },
  { value: 0.1, label: 'Boost (+0.10)' },
  { value: 0.05, label: 'Light boost (+0.05)' },
  { value: 0, label: 'None' },
  { value: -0.05, label: 'Light reduce (-0.05)' },
  { value: -0.1, label: 'Reduce (-0.10)' },
  { value: -0.15, label: 'Strong reduce (-0.15)' },
];

const formatDateValue = (value) => {
  if (!value) return '';
  return String(value).slice(0, 10);
};

const SectionPaper = ({ title, children }) => (
  <Paper variant="outlined" sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}>
    <Typography variant="h6">{title}</Typography>
    <Divider />
    <Box sx={{ flex: 1 }}>{children}</Box>
  </Paper>
);

export default function RelationPriorsManagement() {
  const [pairHelpOpen, setPairHelpOpen] = useState(false);
  const [pairStats, setPairStats] = useState([]);
  const [pairMeta, setPairMeta] = useState({ product_types: [], relation_types: [] });
  const [pairLoading, setPairLoading] = useState(false);
  const [pairError, setPairError] = useState('');
  // 🔴 Welle 3 PR 4 (§4.1): der Env-Name gehoert in den Tooltip.
  const [pairErrorTitle, setPairErrorTitle] = useState('');
  const [pairFilters, setPairFilters] = useState({
    relationType: '',
    anchorType: '',
    candidateType: '',
    query: '',
  });
  const [pairOrderBy, setPairOrderBy] = useState('effective_bias');
  const [pairOrder, setPairOrder] = useState('desc');
  const [pairPage, setPairPage] = useState(0);
  const [pairRowsPerPage, setPairRowsPerPage] = useState(10);
  const [pairSaving, setPairSaving] = useState({});
  const [pairLoaded, setPairLoaded] = useState(false);
  const [pairEdits, setPairEdits] = useState({});
  const [pairEditErrors, setPairEditErrors] = useState({});

  const fetchPairStats = useCallback(async ({ seed } = {}) => {
    if (!ADMIN_KEY) {
      setPairError('Relation priors could not be loaded.');
      setPairErrorTitle('REACT_APP_ADMIN_KEY');
      return;
    }
    setPairLoading(true);
    setPairError('');
    setPairErrorTitle('');
    try {
      const params = new URLSearchParams();
      if (seed) params.set('seed', '1');
      const query = params.toString();
      const { data } = await api.get(`/api/admin/relations/pair-stats${query ? `?${query}` : ''}`, ADMIN_HEADERS);
      const rawItems = Array.isArray(data?.items) ? data.items : [];
      const normalizedItems = rawItems.map((item) => ({
        ...item,
        anchor_type: item.anchor_canonical_type || item.anchor_type || '',
        candidate_type: item.candidate_canonical_type || item.candidate_type || '',
        relation_type: item.relation_type || '',
      }));
      const productTypes =
        Array.isArray(data?.canonical_types) && data.canonical_types.length
          ? data.canonical_types
          : Array.from(
              new Set(
                normalizedItems
                  .flatMap((row) => [row.anchor_type, row.candidate_type])
                  .filter(Boolean)
              )
            );
      const relationTypes =
        Array.isArray(data?.relation_types) && data.relation_types.length
          ? data.relation_types
          : Array.from(new Set(normalizedItems.map((row) => row.relation_type).filter(Boolean)));
      setPairStats(normalizedItems);
      setPairMeta({ product_types: productTypes, relation_types: relationTypes });
      setPairLoaded(true);
    } catch (err) {
      setPairError(err?.response?.data?.error || 'Failed to load relation priors.');
      setPairErrorTitle('');
    } finally {
      setPairLoading(false);
    }
  }, []);

  useEffect(() => {
    if (pairLoaded) return;
    fetchPairStats({ seed: true });
  }, [pairLoaded, fetchPairStats]);

  const updatePairFilter = (field, value) => {
    setPairFilters((prev) => ({ ...prev, [field]: value }));
    setPairPage(0);
  };

  const handlePairSort = (field) => {
    if (pairOrderBy === field) {
      setPairOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setPairOrderBy(field);
    setPairOrder('asc');
  };

  const handlePairBiasChange = async (row, biasValue) => {
    if (!ADMIN_KEY) return;
    const key = row?.id
      ? `pair-${row.id}`
      : `pair-${row.anchor_type || 'unknown'}::${row.candidate_type || 'unknown'}::${row.relation_type || 'unknown'}`;
    setPairSaving((prev) => ({ ...prev, [key]: true }));
    try {
      const { data } = await api.post(
        '/api/admin/relations/pair-stats',
        {
          anchor_canonical_type: row.anchor_type,
          candidate_canonical_type: row.candidate_type,
          relation_type: row.relation_type,
          bias: biasValue,
        },
        ADMIN_HEADERS
      );
      const updated = data?.item;
      if (updated) {
        setPairStats((prev) =>
          prev.map((item) =>
            row?.id
              ? item.id === row.id
              : item.anchor_type === row.anchor_type &&
                item.candidate_type === row.candidate_type &&
                item.relation_type === row.relation_type
              ? {
                  ...item,
                  ...updated,
                  anchor_type: updated.anchor_product_type || item.anchor_type,
                  candidate_type: updated.candidate_product_type || item.candidate_type,
                }
              : item
          )
        );
      }
    } catch (err) {
      setPairError(err?.response?.data?.error || 'Failed to update bias.');
      setPairErrorTitle('');
    } finally {
      setPairSaving((prev) => ({ ...prev, [key]: false }));
    }
  };

  const getPairEditKey = (row) =>
    row?.id
      ? `pair-${row.id}`
      : `pair-${row.anchor_type || 'unknown'}::${row.candidate_type || 'unknown'}::${row.relation_type || 'unknown'}`;

  const getPairEditValue = (row, field, fallback) => {
    const key = getPairEditKey(row);
    const draft = pairEdits[key] || {};
    return draft[field] ?? fallback;
  };

  const setPairEditValue = (row, field, value) => {
    const key = getPairEditKey(row);
    setPairEdits((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [field]: value },
    }));
  };

  const setPairEditError = (row, field, message) => {
    const key = getPairEditKey(row);
    setPairEditErrors((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), [field]: message },
    }));
  };

  const handlePairStatUpdate = async (row, payload) => {
    if (!ADMIN_KEY) return;
    const key = row?.id
      ? `pair-${row.id}`
      : `pair-${row.anchor_type || 'unknown'}::${row.candidate_type || 'unknown'}::${row.relation_type || 'unknown'}`;
    setPairSaving((prev) => ({ ...prev, [key]: true }));
    try {
      const { data } = await api.post(
        '/api/admin/relations/pair-stats',
        {
          anchor_product_type: row.anchor_type,
          candidate_product_type: row.candidate_type,
          relation_type: row.relation_type,
          ...payload,
        },
        ADMIN_HEADERS
      );
      const updated = data?.item;
      if (updated) {
        setPairStats((prev) =>
          prev.map((item) =>
            row?.id
              ? item.id === row.id
              : item.anchor_type === row.anchor_type &&
                item.candidate_type === row.candidate_type &&
                item.relation_type === row.relation_type
              ? {
                  ...item,
                  ...updated,
                  anchor_type: updated.anchor_product_type || item.anchor_type,
                  candidate_type: updated.candidate_product_type || item.candidate_type,
                }
              : item
          )
        );
      }
    } catch (err) {
      setPairError(err?.response?.data?.error || 'Failed to update pair settings.');
      setPairErrorTitle('');
    } finally {
      setPairSaving((prev) => ({ ...prev, [key]: false }));
    }
  };

  const filteredPairStats = useMemo(() => {
    let rows = [...pairStats];
    if (pairFilters.relationType) rows = rows.filter((row) => row.relation_type === pairFilters.relationType);
    if (pairFilters.anchorType) rows = rows.filter((row) => row.anchor_type === pairFilters.anchorType);
    if (pairFilters.candidateType) rows = rows.filter((row) => row.candidate_type === pairFilters.candidateType);
    const query = (pairFilters.query || '').trim().toLowerCase();
    if (query) {
      rows = rows.filter((row) => {
        const haystack = `${row.anchor_type || ''} ${row.candidate_type || ''} ${row.relation_type || ''}`.toLowerCase();
        return haystack.includes(query);
      });
    }
    return rows;
  }, [pairStats, pairFilters]);

  const sortedPairStats = useMemo(() => {
    const rows = [...filteredPairStats];
    rows.sort((a, b) => {
      const aVal = a?.[pairOrderBy];
      const bVal = b?.[pairOrderBy];
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      const isNumber = typeof aVal === 'number' && typeof bVal === 'number';
      const diff = isNumber ? aVal - bVal : String(aVal).localeCompare(String(bVal));
      return pairOrder === 'asc' ? diff : -diff;
    });
    return rows;
  }, [filteredPairStats, pairOrderBy, pairOrder]);

  const pairPageItems = useMemo(() => {
    const start = pairPage * pairRowsPerPage;
    return sortedPairStats.slice(start, start + pairRowsPerPage);
  }, [sortedPairStats, pairPage, pairRowsPerPage]);

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <SectionPaper title="Category relation priors">
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Set category-level bias for relation matching. These priors nudge the product-level matcher (+/- 0.10 max) without
              overriding hard constraints. Use "Prefer" when the pairing is usually valid and "Avoid" for pairs that rarely make sense.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Seasonal and persona biases let you boost or dampen pairings during specific date windows or for specific personas
              (e.g., emphasize portable gear for outdoor visitors).
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button variant="text" onClick={() => setPairHelpOpen(true)}>
                How these priors work
              </Button>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" flexWrap="wrap">
              <Button
                variant="outlined"
                onClick={() => fetchPairStats({ seed: true })}
                disabled={pairLoading}
              >
                {pairLoading ? 'Loading…' : 'Seed & refresh'}
              </Button>
              {/* 🔴 W13 (2026-10-07): beschrifteter „Refresh list" neben
                  „Seed & refresh" — zwei Formen fuer dieselbe Sache. Der reine
                  Neulauf ist jetzt der eine Auffrisch-Knopf (E8); „Seed &
                  refresh" bleibt ein beschrifteter Knopf, weil er etwas
                  ANLEGT.
                  🔴 `firstLoad` (R16 W2): hier stand „`pairLoading` startet auf
                  `false`, ein Erstaufbau faellt also nicht an" — das schliesst
                  vom STARTWERT aufs VERHALTEN und ist gemessen falsch. Der
                  Effekt `:140-143` feuert beim Montieren, ruft
                  `fetchPairStats({ seed: true })` und setzt in `:100`
                  `setPairLoading(true)`. Ein Erstaufbau faellt sehr wohl an.
                  Der Diskriminator ist `pairLoaded` (startet `false`, wird in
                  `:131` nach dem ersten erfolgreichen Abruf `true`) — NICHT
                  allein, weil er bei einem Fehler `false` bliebe und der Knopf
                  dann dauerhaft gesperrt waere: die Verknuepfung mit
                  `pairLoading` gibt ihn nach dem gescheiterten Versuch frei. */}
              <RefreshButton
                ariaLabel="Refresh relation priors"
                loading={pairLoading}
                firstLoad={pairLoading && !pairLoaded}
                onClick={() => fetchPairStats({ seed: false })}
              />
              <Chip
                label={`${sortedPairStats.length} pairs`}
                size="small"
                color="default"
                sx={{ ml: { xs: 0, sm: 'auto' } }}
              />
            </Stack>
            {pairError && (
              <Typography color="error" variant="body2" title={pairErrorTitle || undefined}>
                {pairError}
              </Typography>
            )}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems="center" flexWrap="wrap">
              <TextField
                size="small"
                label="Search types"
                value={pairFilters.query}
                onChange={(event) => updatePairFilter('query', event.target.value)}
                sx={{ minWidth: 200 }}
              />
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="pair-anchor-type-label">Anchor type</InputLabel>
                <Select
                  labelId="pair-anchor-type-label"
                  label="Anchor type"
                  value={pairFilters.anchorType}
                  onChange={(event) => updatePairFilter('anchorType', event.target.value)}
                >
                  <MenuItem value="">
                    <em>All anchors</em>
                  </MenuItem>
                  {pairMeta.product_types.map((type) => (
                    <MenuItem key={`anchor-${type}`} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="pair-candidate-type-label">Candidate type</InputLabel>
                <Select
                  labelId="pair-candidate-type-label"
                  label="Candidate type"
                  value={pairFilters.candidateType}
                  onChange={(event) => updatePairFilter('candidateType', event.target.value)}
                >
                  <MenuItem value="">
                    <em>All candidates</em>
                  </MenuItem>
                  {pairMeta.product_types.map((type) => (
                    <MenuItem key={`candidate-${type}`} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="text"
                onClick={() => setPairFilters({ relationType: '', anchorType: '', candidateType: '', query: '' })}
              >
                Clear filters
              </Button>
            </Stack>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {pairMeta.relation_types.map((type) => (
                <Chip
                  key={`relation-${type}`}
                  label={type}
                  color={pairFilters.relationType === type ? 'primary' : 'default'}
                  onClick={() =>
                    updatePairFilter('relationType', pairFilters.relationType === type ? '' : type)
                  }
                />
              ))}
            </Stack>
            <TableViewport footerPadPx={0} component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sortDirection={pairOrderBy === 'anchor_type' ? pairOrder : false}>
                      <TableSortLabel
                        active={pairOrderBy === 'anchor_type'}
                        direction={pairOrderBy === 'anchor_type' ? pairOrder : 'asc'}
                        onClick={() => handlePairSort('anchor_type')}
                      >
                        <Tooltip title="Anchor product_type = the product being viewed. Example: grinder_electric." arrow>
                          <span>Anchor type</span>
                        </Tooltip>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={pairOrderBy === 'candidate_type' ? pairOrder : false}>
                      <TableSortLabel
                        active={pairOrderBy === 'candidate_type'}
                        direction={pairOrderBy === 'candidate_type' ? pairOrder : 'asc'}
                        onClick={() => handlePairSort('candidate_type')}
                      >
                        <Tooltip title="Candidate product_type = the recommended product. Example: burrs, equipment_accessory." arrow>
                          <span>Candidate type</span>
                        </Tooltip>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={pairOrderBy === 'relation_type' ? pairOrder : false}>
                      <TableSortLabel
                        active={pairOrderBy === 'relation_type'}
                        direction={pairOrderBy === 'relation_type' ? pairOrder : 'asc'}
                        onClick={() => handlePairSort('relation_type')}
                      >
                        <Tooltip title="Relation type the bias applies to (complement/addon/competitor)." arrow>
                          <span>Relation</span>
                        </Tooltip>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={pairOrderBy === 'approvals' ? pairOrder : false} align="right">
                      <TableSortLabel
                        active={pairOrderBy === 'approvals'}
                        direction={pairOrderBy === 'approvals' ? pairOrder : 'asc'}
                        onClick={() => handlePairSort('approvals')}
                      >
                        <Tooltip title="Count of accepted recommendations for this pair, captured from real usage." arrow>
                          <span>Approvals</span>
                        </Tooltip>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={pairOrderBy === 'rejections' ? pairOrder : false} align="right">
                      <TableSortLabel
                        active={pairOrderBy === 'rejections'}
                        direction={pairOrderBy === 'rejections' ? pairOrder : 'asc'}
                        onClick={() => handlePairSort('rejections')}
                      >
                        <Tooltip title="Number of manual rejections logged for this pair." arrow>
                          <span>Rejections</span>
                        </Tooltip>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={pairOrderBy === 'bias' ? pairOrder : false} align="right">
                      <TableSortLabel
                        active={pairOrderBy === 'bias'}
                        direction={pairOrderBy === 'bias' ? pairOrder : 'asc'}
                        onClick={() => handlePairSort('bias')}
                      >
                        <Tooltip title="Manual bias applied to match_score (+/- 0.10)." arrow>
                          <span>Manual bias</span>
                        </Tooltip>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={pairOrderBy === 'effective_bias' ? pairOrder : false} align="right">
                      <TableSortLabel
                        active={pairOrderBy === 'effective_bias'}
                        direction={pairOrderBy === 'effective_bias' ? pairOrder : 'asc'}
                        onClick={() => handlePairSort('effective_bias')}
                      >
                        <Tooltip title="Manual bias plus approval/rejection learning. Clamped to +/- 0.15." arrow>
                          <span>Effective bias</span>
                        </Tooltip>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={pairOrderBy === 'seasonal_bias' ? pairOrder : false} align="right">
                      <TableSortLabel
                        active={pairOrderBy === 'seasonal_bias'}
                        direction={pairOrderBy === 'seasonal_bias' ? pairOrder : 'asc'}
                        onClick={() => handlePairSort('seasonal_bias')}
                      >
                        <Tooltip title="Extra bias applied only within the seasonal window. Clamped to +/- 0.20." arrow>
                          <span>Seasonal bias</span>
                        </Tooltip>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={pairOrderBy === 'seasonal_start' ? pairOrder : false}>
                      <TableSortLabel
                        active={pairOrderBy === 'seasonal_start'}
                        direction={pairOrderBy === 'seasonal_start' ? pairOrder : 'asc'}
                        onClick={() => handlePairSort('seasonal_start')}
                      >
                        <Tooltip title="Season start date (YYYY-MM-DD) for applying seasonal bias." arrow>
                          <span>Season start</span>
                        </Tooltip>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={pairOrderBy === 'seasonal_end' ? pairOrder : false}>
                      <TableSortLabel
                        active={pairOrderBy === 'seasonal_end'}
                        direction={pairOrderBy === 'seasonal_end' ? pairOrder : 'asc'}
                        onClick={() => handlePairSort('seasonal_end')}
                      >
                        <Tooltip title="Season end date (YYYY-MM-DD) for applying seasonal bias." arrow>
                          <span>Season end</span>
                        </Tooltip>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sortDirection={pairOrderBy === 'persona_bias' ? pairOrder : false}>
                      <TableSortLabel
                        active={pairOrderBy === 'persona_bias'}
                        direction={pairOrderBy === 'persona_bias' ? pairOrder : 'asc'}
                        onClick={() => handlePairSort('persona_bias')}
                      >
                        <Tooltip
                          title="JSON map of persona bias (example: outdoor=0.05). Applied when persona filter is active."
                          arrow
                        >
                          <span>Persona bias</span>
                        </Tooltip>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">Manual bias</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pairLoading && (
                    <TableRow>
                      <TableCell colSpan={12}>
                        <Typography variant="body2">Loading pairs…</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {!pairLoading && pairPageItems.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={12}>
                        <Typography variant="body2">No pairs match those filters yet.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {pairPageItems.map((row, index) => {
                    const rowKey = row?.id
                      ? `pair-${row.id}`
                      : `pair-${row.anchor_type || 'unknown'}::${row.candidate_type || 'unknown'}::${row.relation_type || 'unknown'}::${index}`;
                    const saveKey = row?.id
                      ? `pair-${row.id}`
                      : `pair-${row.anchor_type || 'unknown'}::${row.candidate_type || 'unknown'}::${row.relation_type || 'unknown'}`;
                    const biasValue = row.bias ?? 0;
                    const seasonalBiasValue = Number(getPairEditValue(row, 'seasonal_bias', row.seasonal_bias ?? 0));
                    const seasonalStartValue = getPairEditValue(row, 'seasonal_start', formatDateValue(row.seasonal_start));
                    const seasonalEndValue = getPairEditValue(row, 'seasonal_end', formatDateValue(row.seasonal_end));
                    const personaBiasValue = getPairEditValue(
                      row,
                      'persona_bias',
                      row.persona_bias ? JSON.stringify(row.persona_bias) : ''
                    );
                    const personaError = pairEditErrors[saveKey]?.persona_bias;
                    const canEdit = Boolean(row.anchor_type && row.candidate_type && row.relation_type);
                    return (
                      <TableRow key={rowKey}>
                        <TableCell>{row.anchor_type || '—'}</TableCell>
                        <TableCell>{row.candidate_type || '—'}</TableCell>
                        <TableCell>{row.relation_type || '—'}</TableCell>
                        <TableCell align="right">{row.approvals ?? 0}</TableCell>
                        <TableCell align="right">{row.rejections ?? 0}</TableCell>
                        <TableCell align="right">{typeof row.bias === 'number' ? formatNumber(row.bias) : '—'}</TableCell>
                        <TableCell align="right">
                          {typeof row.effective_bias === 'number' ? formatNumber(row.effective_bias) : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {/* 🔴 Nur das ERSTE gesperrte Feld der Zeile nennt den
                              Grund. `canEdit` gilt fuer die GANZE Tabelle; ohne
                              diese Regel truege jede Zeile FUENF identische
                              Halter mit je `tabIndex={0}` — fuenf Tab-Stopps
                              mehr je Zeile, alle mit demselben Satz (R15 W2).
                              Die vier uebrigen Felder bleiben schlicht gesperrt:
                              `disabled` ist unveraendert, nur der Halter faellt. */}
                          <DisabledReason
                            reason={pairSaving[saveKey]
                              ? null
                              : !canEdit ? 'Set anchor type, candidate type and relation type first.' : null}
                          >
                            <FormControl size="small" sx={{ minWidth: 170 }}>
                              <Select
                                value={seasonalBiasValue}
                                onChange={(event) => {
                                  const value = Number(event.target.value);
                                  setPairEditValue(row, 'seasonal_bias', value);
                                  handlePairStatUpdate(row, { seasonal_bias: value });
                                }}
                                disabled={!canEdit || pairSaving[saveKey]}
                              >
                                {SEASONAL_BIAS_OPTIONS.map((opt) => (
                                  <MenuItem key={`seasonal-bias-${opt.value}`} value={opt.value}>
                                    {opt.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </DisabledReason>
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="date"
                            size="small"
                            value={seasonalStartValue}
                            onChange={(event) => setPairEditValue(row, 'seasonal_start', event.target.value)}
                            onBlur={() =>
                              handlePairStatUpdate(row, {
                                seasonal_start: seasonalStartValue ? `${seasonalStartValue}T00:00:00` : null,
                              })
                            }
                            disabled={!canEdit || pairSaving[saveKey]}
                            sx={{ minWidth: 140 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="date"
                            size="small"
                            value={seasonalEndValue}
                            onChange={(event) => setPairEditValue(row, 'seasonal_end', event.target.value)}
                            onBlur={() =>
                              handlePairStatUpdate(row, {
                                seasonal_end: seasonalEndValue ? `${seasonalEndValue}T00:00:00` : null,
                              })
                            }
                            disabled={!canEdit || pairSaving[saveKey]}
                            sx={{ minWidth: 140 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            multiline
                            minRows={1}
                            maxRows={3}
                            value={personaBiasValue}
                            onChange={(event) => {
                              setPairEditValue(row, 'persona_bias', event.target.value);
                              if (pairEditErrors[saveKey]?.persona_bias) {
                                setPairEditError(row, 'persona_bias', '');
                              }
                            }}
                            onBlur={() => {
                              const trimmed = (personaBiasValue || '').trim();
                              if (!trimmed) {
                                setPairEditError(row, 'persona_bias', '');
                                handlePairStatUpdate(row, { persona_bias: null });
                                return;
                              }
                              try {
                                const parsed = JSON.parse(trimmed);
                                setPairEditError(row, 'persona_bias', '');
                                handlePairStatUpdate(row, { persona_bias: parsed });
                              } catch (err) {
                                setPairEditError(row, 'persona_bias', 'Invalid JSON');
                              }
                            }}
                            error={Boolean(personaError)}
                            helperText={personaError || 'Example: {"outdoor": 0.05}'}
                            disabled={!canEdit || pairSaving[saveKey]}
                            sx={{ minWidth: 220 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <FormControl size="small" sx={{ minWidth: 160 }}>
                            <Select
                              value={biasValue}
                              onChange={(event) => handlePairBiasChange(row, Number(event.target.value))}
                              disabled={!canEdit || pairSaving[saveKey]}
                            >
                              {RELATION_BIAS_OPTIONS.map((opt) => (
                                <MenuItem key={`bias-${opt.value}`} value={opt.value}>
                                  {opt.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableViewport>
            <TablePagination
              component="div"
              count={sortedPairStats.length}
              page={pairPage}
              onPageChange={(event, newPage) => setPairPage(newPage)}
              rowsPerPage={pairRowsPerPage}
              rowsPerPageOptions={[10, 20, 50]}
              onRowsPerPageChange={(event) => {
                setPairRowsPerPage(parseInt(event.target.value, 10));
                setPairPage(0);
              }}
            />
          </Stack>
          <Dialog open={pairHelpOpen} onClose={() => setPairHelpOpen(false)} maxWidth="md" fullWidth>
            <DialogTitle>How category relation priors affect recommendations</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2}>
                <Typography variant="body2">
                  Category priors are a lightweight way to bias recommendations at the product-type level before
                  product-level scoring runs. They do not override hard constraints (e.g. brew compatibility, burr fit).
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="Anchor vs candidate"
                      secondary="Anchor type = the product being viewed. Candidate type = the recommended product. Example: anchor=grinder_electric, candidate=burrs."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Relation types"
                      secondary="Complement = core add-on or upgrade, Addon = small accessory/consumable, Competitor = same core function alternatives."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Same type pairs"
                      secondary="Same product_type usually means competitor (e.g. grinder_electric → grinder_electric). Complement for same type should be rare and intentional."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Scoring impact"
                      secondary="Manual bias is applied to match_score (±0.10). Effective bias = manual bias + approval/rejection signal + seasonal bias, clamped to ±0.20. Upsell score gets half of the combined bias."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Seasonal + persona bias"
                      secondary="Seasonal bias applies only between the start/end dates. Persona bias is a JSON map of persona → bias; only applied when a persona filter is active."
                    />
                  </ListItem>
                </List>
                <Typography variant="subtitle2">Examples</Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="grinder_electric → burrs (complement)"
                      secondary="Prefer (+0.10) if you commonly sell burr upgrades for grinders."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="espresso_machine → equipment_accessory (complement)"
                      secondary="Prefer (+0.05) if accessories like tampers and scales are usually valid add-ons."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="grinder_hand → grinder_hand (competitor)"
                      secondary="Prefer (+0.05) to surface alternative hand grinders as comparisons."
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="burrs → grinder_hand (complement)"
                      secondary="Avoid (-0.10) if hand grinders are never compatible with burr upgrades."
                    />
                  </ListItem>
                </List>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPairHelpOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>
        </SectionPaper>
      </Grid>
    </Grid>
  );
}
